import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, CalendarClock, MessageCircle, Mail, Send, CheckCircle2, Pencil, Plus } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const formatDate = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (s || '-');
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(n || 0);

export default function ReservasPage() {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('pendientes');
  const [selected, setSelected] = useState([]);
  const [config, setConfig] = useState({ template_whatsapp: '', template_email_asunto: '', template_email: '' });
  const [templateOpen, setTemplateOpen] = useState(false);
  const [avisoOpen, setAvisoOpen] = useState(false);
  const [avisoTargets, setAvisoTargets] = useState([]);
  const [sending, setSending] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const emptyManual = {
    nombre_completo: '', rut: '', telefono: '', email: '',
    vacuna: '', dosis: '', fecha_reserva: new Date().toISOString().slice(0, 10),
    monto: 0, comentarios: ''
  };
  const [manual, setManual] = useState(emptyManual);
  const [editingId, setEditingId] = useState(null);

  const openEdit = (r) => {
    setEditingId(r.id);
    setManual({
      nombre_completo: r.nombre_completo || '',
      rut: r.rut || '',
      telefono: r.telefono || '',
      email: r.email || '',
      vacuna: r.vacuna || '',
      dosis: r.dosis || '',
      fecha_reserva: (r.fecha_vacunacion || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
      monto: r.monto ?? 0,
      comentarios: r.comentarios || ''
    });
    setManualOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setManual(emptyManual);
    setManualOpen(true);
  };

  const saveManual = async () => {
    if (!manual.nombre_completo.trim() || !manual.vacuna.trim()) {
      toast.error('Nombre del paciente y vacuna son obligatorios');
      return;
    }
    setSavingManual(true);
    try {
      const payload = { ...manual, monto: parseFloat(manual.monto) || 0 };
      if (editingId) {
        await axios.put(`${API}/reservas/${editingId}`, payload, { withCredentials: true });
        toast.success('Reserva actualizada');
      } else {
        await axios.post(`${API}/reservas/manual`, payload, { withCredentials: true });
        toast.success('Reserva histórica agregada');
      }
      setManualOpen(false);
      setManual(emptyManual);
      setEditingId(null);
      fetchReservas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al agregar la reserva');
    } finally {
      setSavingManual(false);
    }
  };

  const fetchReservas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reservas?incluir_vacunados=${vista === 'todas'}`, { withCredentials: true });
      setReservas(res.data);
      setSelected([]);
    } catch (error) {
      toast.error('Error al cargar las reservas');
    } finally {
      setLoading(false);
    }
  }, [vista]);

  useEffect(() => { fetchReservas(); }, [fetchReservas]);

  useEffect(() => {
    axios.get(`${API}/reservas/config`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => {});
  }, []);

  const pendientes = reservas.filter(r => r.estado === 'RESERVADA');
  const allSelected = pendientes.length > 0 && selected.length === pendientes.length;

  const toggleSelect = (id) =>
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const toggleSelectAll = () =>
    setSelected(allSelected ? [] : pendientes.map(r => r.id));

  const openAviso = (targets) => {
    setAvisoTargets(targets);
    setAvisoOpen(true);
  };

  const sendAvisos = async (canal) => {
    setSending(true);
    let ok = 0, fail = 0;
    const errors = [];
    for (const r of avisoTargets) {
      try {
        await axios.post(`${API}/reservas/${r.id}/avisar`, { canal }, { withCredentials: true });
        ok++;
      } catch (e) {
        fail++;
        errors.push(`${r.nombre_completo}: ${e.response?.data?.detail || 'error'}`);
      }
    }
    setSending(false);
    setAvisoOpen(false);
    if (ok) toast.success(`${ok} aviso(s) enviado(s) por ${canal === 'whatsapp' ? 'WhatsApp' : 'correo'}`);
    if (fail) toast.error(`${fail} aviso(s) fallaron: ${errors.slice(0, 3).join(' | ')}`);
    fetchReservas();
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/reservas/config`, config, { withCredentials: true });
      toast.success('Plantilla guardada');
      setTemplateOpen(false);
    } catch (error) {
      toast.error('Error al guardar la plantilla');
    }
  };

  const enviarAVacunar = async (r) => {
    try {
      await axios.put(`${API}/reservas/${r.id}/estado`, { estado: 'BORRADOR' }, { withCredentials: true });
      toast.success(`${r.nombre_completo} enviado a Pendientes de aplicar`);
      fetchReservas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const estadoBadge = (estado) => {
    if (estado === 'VACUNADO') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Vacunado</Badge>;
    if (estado === 'BORRADOR') return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">En vacunación</Badge>;
    return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Reservada</Badge>;
  };

  const avisoBadge = (r) => {
    const a = r.aviso_llegada;
    if (!a) return <Badge variant="outline" className="text-slate-500">Sin aviso</Badge>;
    const canal = a.canal === 'whatsapp' ? 'WhatsApp' : 'Correo';
    return (
      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
        {canal} {formatDate(a.enviado_at)}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="reservas-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarClock className="w-6 h-6 text-blue-600" />
              Reservas
            </h1>
            <p className="text-slate-500">Vacunas reservadas y pagadas pendientes de administración</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={vista} onValueChange={setVista}>
              <SelectTrigger className="w-[160px]" data-testid="reservas-vista-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pendientes">Pendientes</SelectItem>
                <SelectItem value="todas">Todas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={openNew} className="gap-2" data-testid="add-reserva-btn">
              <Plus className="w-4 h-4" />
              Agregar Reserva
            </Button>
            <Button variant="outline" onClick={() => setTemplateOpen(true)} className="gap-2" data-testid="edit-template-btn">
              <Pencil className="w-4 h-4" />
              Editar Plantilla
            </Button>
            <Button
              onClick={() => openAviso(pendientes.filter(r => selected.includes(r.id)))}
              disabled={selected.length === 0}
              className="gap-2"
              data-testid="avisar-seleccionados-btn"
            >
              <Send className="w-4 h-4" />
              Avisar seleccionados ({selected.length})
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {vista === 'pendientes' ? `Reservas pendientes (${pendientes.length})` : `Todas las reservas (${reservas.length})`}
            </CardTitle>
            <CardDescription>Tipo de vacunación: PRIV. RESERVADA Y PAGADA</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
              </div>
            ) : reservas.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="reservas-empty">
                <CalendarClock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay reservas {vista === 'pendientes' ? 'pendientes' : 'registradas'}</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table data-testid="reservas-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                      </TableHead>
                      <TableHead>Paciente</TableHead>
                      <TableHead>RUT</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead>Correo</TableHead>
                      <TableHead>Vacuna</TableHead>
                      <TableHead>Fecha Reserva</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Aviso</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reservas.map(r => (
                      <TableRow key={r.id} data-testid={`reserva-row-${r.id}`}>
                        <TableCell>
                          {r.estado === 'RESERVADA' && (
                            <Checkbox
                              checked={selected.includes(r.id)}
                              onCheckedChange={() => toggleSelect(r.id)}
                              data-testid={`select-checkbox-${r.id}`}
                            />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{r.nombre_completo}</TableCell>
                        <TableCell className="font-mono text-sm">{r.rut}</TableCell>
                        <TableCell className="text-sm">{r.telefono || '-'}</TableCell>
                        <TableCell className="text-sm">{r.email || '-'}</TableCell>
                        <TableCell>{r.vacuna}</TableCell>
                        <TableCell>{formatDate(r.fecha_vacunacion)}</TableCell>
                        <TableCell className="font-mono text-sm">{formatCurrency(r.monto)}</TableCell>
                        <TableCell>{avisoBadge(r)}</TableCell>
                        <TableCell>{estadoBadge(r.estado)}</TableCell>
                        <TableCell className="text-right">
                          {r.estado === 'RESERVADA' && (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEdit(r)}
                                className="gap-1"
                                data-testid={`editar-btn-${r.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                Editar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openAviso([r])}
                                className="gap-1"
                                data-testid={`avisar-btn-${r.id}`}
                              >
                                <Send className="w-3.5 h-3.5" />
                                Avisar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                onClick={() => enviarAVacunar(r)}
                                data-testid={`vacunar-btn-${r.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Vacunar
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog: elegir canal de aviso */}
        <Dialog open={avisoOpen} onOpenChange={setAvisoOpen}>
          <DialogContent data-testid="aviso-dialog">
            <DialogHeader>
              <DialogTitle>Aviso "Vacuna Llegó"</DialogTitle>
              <DialogDescription>
                Enviar aviso a {avisoTargets.length} paciente(s): elige el canal de envío
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-40 overflow-y-auto text-sm text-slate-600">
              {avisoTargets.map(r => (
                <div key={r.id} className="flex justify-between border-b pb-1">
                  <span>{r.nombre_completo}</span>
                  <span className="text-slate-400">{r.vacuna}</span>
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                onClick={() => sendAvisos('whatsapp')}
                disabled={sending}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="send-whatsapp-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                WhatsApp
              </Button>
              <Button
                onClick={() => sendAvisos('email')}
                disabled={sending}
                className="gap-2"
                data-testid="send-email-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Correo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: agregar reserva histórica */}
        <Dialog open={manualOpen} onOpenChange={setManualOpen}>
          <DialogContent className="max-w-2xl" data-testid="manual-reserva-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar Reserva' : 'Agregar Reserva Histórica'}</DialogTitle>
              <DialogDescription>
                {editingId
                  ? 'Modifica los datos de la reserva'
                  : 'Para reservas pagadas anteriormente (incluso de años anteriores) que no están en el sistema. No genera cobro.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre completo *</Label>
                <Input value={manual.nombre_completo} onChange={(e) => setManual({ ...manual, nombre_completo: e.target.value })} data-testid="manual-nombre-input" />
              </div>
              <div className="space-y-2">
                <Label>RUT</Label>
                <Input value={manual.rut} onChange={(e) => setManual({ ...manual, rut: e.target.value })} placeholder="12.345.678-9" data-testid="manual-rut-input" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={manual.telefono} onChange={(e) => setManual({ ...manual, telefono: e.target.value })} placeholder="+569..." data-testid="manual-telefono-input" />
              </div>
              <div className="space-y-2">
                <Label>Correo</Label>
                <Input value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} data-testid="manual-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Vacuna reservada *</Label>
                <Input value={manual.vacuna} onChange={(e) => setManual({ ...manual, vacuna: e.target.value })} placeholder="Ej: HERPES ZOSTER" data-testid="manual-vacuna-input" />
              </div>
              <div className="space-y-2">
                <Label>Dosis</Label>
                <Input value={manual.dosis} onChange={(e) => setManual({ ...manual, dosis: e.target.value })} placeholder="Ej: 1RA" data-testid="manual-dosis-input" />
              </div>
              <div className="space-y-2">
                <Label>Fecha de la reserva/pago original</Label>
                <Input type="date" value={manual.fecha_reserva} onChange={(e) => setManual({ ...manual, fecha_reserva: e.target.value })} data-testid="manual-fecha-input" />
              </div>
              <div className="space-y-2">
                <Label>Monto ($)</Label>
                <Input type="number" value={manual.monto} onChange={(e) => setManual({ ...manual, monto: e.target.value })} data-testid="manual-monto-input" />
                <p className="text-xs text-slate-500">Deja 0 si ya fue pagada anteriormente (no afecta la caja actual)</p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Comentarios</Label>
                <Textarea rows={2} value={manual.comentarios} onChange={(e) => setManual({ ...manual, comentarios: e.target.value })} data-testid="manual-comentarios-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setManualOpen(false)}>Cancelar</Button>
              <Button onClick={saveManual} disabled={savingManual} data-testid="save-manual-btn">
                {savingManual && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingId ? 'Guardar Cambios' : 'Agregar Reserva'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: editar plantilla */}
        <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
          <DialogContent className="max-w-2xl" data-testid="template-dialog">
            <DialogHeader>
              <DialogTitle>Plantilla del Aviso "Vacuna Llegó"</DialogTitle>
              <DialogDescription>
                Variables disponibles: {'{nombre}'}, {'{vacuna}'}, {'{fecha}'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Mensaje WhatsApp</Label>
                <Textarea
                  rows={4}
                  value={config.template_whatsapp || ''}
                  onChange={(e) => setConfig({ ...config, template_whatsapp: e.target.value })}
                  data-testid="template-whatsapp-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Asunto del Correo</Label>
                <Input
                  value={config.template_email_asunto || ''}
                  onChange={(e) => setConfig({ ...config, template_email_asunto: e.target.value })}
                  data-testid="template-email-asunto-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Cuerpo del Correo</Label>
                <Textarea
                  rows={6}
                  value={config.template_email || ''}
                  onChange={(e) => setConfig({ ...config, template_email: e.target.value })}
                  data-testid="template-email-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTemplateOpen(false)}>Cancelar</Button>
              <Button onClick={saveConfig} data-testid="save-template-btn">Guardar Plantilla</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
